/**
 * X12 849 footer AMT qualifiers that carry tax.
 *
 * Shared across the backend tax extractor (`edi849TaxStrategies.ts`) and the
 * frontend raw-EDI header parser (`ediHeaderParser.ts`) so the two layers
 * reference a single source of truth and tax parsing can't drift between them.
 * (`AMT*1` = per-line amount and `AMT*2` = batch total are NOT tax.)
 */
export const TAX_AMT_QUALIFIER = {
  /** HST (single-rate HST provinces) */
  COMBINED: "T",
  /** GST (Goods & Services Tax) */
  GST: "GT",
  /** FST / HST */
  FST: "F7",
  /** PST / QST (Provincial Sales Tax) */
  PST: "ST",
} as const;

/**
 * Invoice master data returned with an E844SD transaction detail.
 * Sourced from rexel_invoice_master, joined by productNumber + company.
 * Dates are stored as 6-digit YYMMDD integers (e.g. 260317 = 2026-03-17).
 */
export interface InvoiceMasterData {
  productNumber: string | null;
  invoiceNumber: string;
  invoiceDate: number | null;
  invoiceDescription: string | null;
  amount: number | null;
  cost: number | null;
  poCost: number | null;
  quantity: number | null;
  division: string | null;
  location: string | null;
  supplierNumber: string | null;
  poNumber: string | null;
}

/**
 * Hardcoded filter options for S&D pending transactions.
 *
 * Shared between backend (getFilterOptionsProcedure) and frontend
 * (AvailableTransactionsSection). Update when new companies, divisions,
 * ship types, or trading partners are added.
 */
export const SD_FILTER_OPTIONS = {
  company: ["Q", "R", "W"],
  division: ["+", "A", "B", "E", "H", "J", "O", "R", "T"],
  ship_type: ["1", "2", "3", "I1", "I2", "I3"],
  rebate_type: ["C", "M", "P"],
  selling_location: [
    "!",
    '"',
    "#",
    "$",
    "%",
    "&",
    "(",
    ")",
    "*",
    "+",
    "-",
    ";",
    "<",
    "=",
    ">",
    "0",
    "1",
    "2",
    "3",
    "4",
    "6",
    "7",
    "8",
    "9",
    "@",
    "A",
    "B",
    "C",
    "D",
    "E",
    "F",
    "G",
    "H",
    "I",
    "J",
    "K",
    "L",
    "M",
    "N",
    "O",
    "P",
    "Q",
    "R",
    "S",
    "T",
    "U",
    "V",
    "W",
    "X",
    "Y",
    "Z",
    "a",
    "c",
    "d",
    "e",
    "f",
    "h",
    "j",
    "k",
    "l",
    "m",
    "n",
    "q",
    "r",
    "t",
    "u",
    "v",
    "w",
    "y",
  ],
  trading_partner: [
    "001288364I",
    "201484300",
    "3M",
    "ABB",
    "ANAMET",
    "APPLETON",
    "BELDEN",
    "BUSSMANN",
    "COMMSCOPE(CAD)",
    "COMMSCOPE(USD)",
    "COOPERWIRING",
    "CRCCANADA",
    "CRCROUSE",
    "EATON",
    "EGSELECTRICAL",
    "ERICO",
    "HAMMOND",
    "HAMMONDPOWER",
    "HOFFMAN",
    "HUBBELL",
    "IPEXEDI",
    "LEDVANCE",
    "LEVITON",
    "LITELINE",
    "NONEDIDFT",
    "NVENT",
    "OMRON",
    "OUELLETCAN",
    "PANDUITEDI",
    "PHOENIXEDIP",
    "RITTAL",
    "ROCKWELL",
    "SCHNEIDER",
    "STANDARDPROD",
    "STELPRODESIGN",
    "SUPERIORESSEX",
    "TESELECTRIC",
    "TYCO",
  ],
};
